import { Code } from '@mantine/core';
import classNames from 'classnames';
import { Fragment, forwardRef, type ReactNode, useImperativeHandle, useMemo, useState } from 'react';
import { ExportImportStatus } from '@/common/graphql';
import { StringUtils } from '@/common/utils';
import { EMPTY_DEFAULT } from '../../table';
import { Cell, CellValueProgress, HistoryList, type HistoryListHandle } from '../components';
import { EMPLOYEE_IMPORT_FORMAT_LABELS } from './const';
import type { ImportHistoryRowData } from './types';
import { useImportHistory } from './useImportHistory';

interface Props {
  modalId?: string;
  onClose?: () => void;
}

/**
 * Unified import history list for both Employee and Managerial Accounting imports.
 * Wraps the shared HistoryList with import-specific columns (format derived from __typename) and
 * uses the unified listImportHistories query / importProgress subscription.
 */
export const ImportHistoryList = forwardRef<HistoryListHandle, Props>(({ modalId, onClose }, ref) => {
  const [onlyMyTask, setOnlyMyTask] = useState(true);
  const { loading, rows, refetch } = useImportHistory(onlyMyTask);

  useImperativeHandle(ref, () => ({ refetch }), [refetch]);

  return (
    <HistoryList
      ref={ref}
      classNames={{
        gridCols: 'grid-cols-[200px_auto_100px_1fr]',
        gridColSpanWhenEmpty: 'col-span-4',
      }}
      columns={{
        secondHeader: headerClassNames => <div className={classNames(headerClassNames, 'px-2')}>Format</div>,
        secondData: row => (
          <Cell className="px-2 items-center justify-start text-xs whitespace-nowrap">
            {row.__typename === 'EmployeeImportHistory'
              ? row.format
                ? EMPLOYEE_IMPORT_FORMAT_LABELS[row.format]
                : EMPTY_DEFAULT
              : 'Managerial Accounting'}
          </Cell>
        ),
        resultData: row =>
          row.status === ExportImportStatus.Failed ? (
            <ul className="text-xs list-disc pl-4 [&_li:not(:last-of-type)]:mb-1">
              {row.errors.map((error, index) => (
                <li key={index}>
                  <ErrorMessage error={error} />
                </li>
              ))}
            </ul>
          ) : row.status === ExportImportStatus.Done ? (
            row.totalRequestCount && row.completedRequestCount > 0 ? (
              <>{StringUtils.withPluralize(row.completedRequestCount, 'request', 'requests')}&nbsp;processed.</>
            ) : (
              ''
            )
          ) : (
            <CellValueProgress
              totalRequestCount={row.totalRequestCount}
              completedRequestCount={row.completedRequestCount}
            />
          ),
      }}
      emptyMessage="Imports you start will appear here."
      loading={loading}
      modalId={modalId}
      rows={rows}
      refetch={refetch}
      onChangeOnlyMyTask={setOnlyMyTask}
      onClose={onClose}
    />
  );
});

const ErrorMessage = ({ error }: { error: ImportHistoryRowData['errors'][number] }) => {
  // noinspection com.intellij.reactbuddy.ExhaustiveDepsInspection
  const metadata = useMemo(() => {
    switch (error.__typename) {
      case 'EmployeeImportHistoryError':
        return (
          error.columnName && (
            <>
              Column <Code>{error.columnName}</Code>
              <br />
            </>
          )
        );
      case 'ManagerialAccountingImportHistoryError': {
        const list: ReactNode[] = [];
        if (error.year) {
          list.push(
            <>
              Year <Code>{error.year}</Code>
            </>,
          );
        }
        if (error.quarter) {
          list.push(
            <>
              Quarter <Code>{error.quarter}</Code>
            </>,
          );
        }
        if (error.employee) {
          list.push(
            <>
              Employee <Code>{error.employee.generalInformation.preferredFullName}</Code>
            </>,
          );
        }
        return (
          <>
            {list.map((item, index) => (
              <Fragment key={index}>
                {index > 0 && ', '}
                {item}
              </Fragment>
            ))}
          </>
        );
      }
    }
  }, [error]);

  return (
    <>
      <div className="text-3xs">
        {error.rowNumber && (
          <>
            Row <Code>{error.rowNumber}</Code>,&nbsp;
          </>
        )}
        {metadata}
      </div>
      {error.message}
    </>
  );
};

ImportHistoryList.displayName = 'ImportHistoryList';
